import type { PropsWithChildren } from 'react';
import React from 'react';

import { environment } from '~app/environment';

import { AuthByPhone } from '~widgets/auth-by-phone/auth-by-phone';
import { RepeatedAuth } from '~widgets/repeated-auth/repeated-auth';

import {
  isAuthorisedAtom,
  isFirstAuthAtom,
  userPhoneAtom,
} from '~entities/auth';

import { ScrollIntoView } from '~shared/ui/scroll-into-view';

import {
  CaptchaContainer,
  CaptchaProvider,
} from '@breezeventures/web-ph-aws-waf-tools/ui';
import { useAtomValue } from 'jotai';

type Props = {
  phoneNumber?: string;
  skipCheck?: boolean;
  onLogin?: (phone: string) => void;
  onCancel?: () => void;
};

export const AuthGuard: React.FC<PropsWithChildren & Props> = React.memo(
  ({ children, phoneNumber, skipCheck, onLogin, onCancel }) => {
    const isAuthorised = useAtomValue(isAuthorisedAtom);

    const lastUserPhoneNumber = useAtomValue(userPhoneAtom);
    const isFirstAuth = useAtomValue(isFirstAuthAtom);

    if (skipCheck) {
      return children;
    }

    if (!isAuthorised) {
      // if not first auth -> show client confirmation with warning instead
      return (
        <CaptchaProvider captchaApiKey={environment.AWS_WAF_API_KEY}>
          <CaptchaContainer>
            <ScrollIntoView>
              {isFirstAuth || !lastUserPhoneNumber ? (
                <AuthByPhone
                  initialPhone={phoneNumber ?? lastUserPhoneNumber ?? undefined}
                  onLogin={onLogin}
                  onCancel={onCancel}
                />
              ) : (
                <RepeatedAuth phone={lastUserPhoneNumber} onLogin={onLogin} />
              )}
            </ScrollIntoView>
          </CaptchaContainer>
        </CaptchaProvider>
      );
    }

    return children;
  }
);
